import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './PermitForm.css';
import { API_BASE } from '../config/apiConfig';

const ROWPermitForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [detailedDrawing, setDetailedDrawing] = useState(null);
  const [formData, setFormData] = useState({
    permitType: 'row',
    // Applicant Information
    applicantName: '',
    applicantPhone: '',
    applicantEmail: '',
    applicantStreetAddress: '',
    applicantStreetAddressLine2: '',
    applicantCity: '',
    applicantState: 'IN',
    applicantZip: '',
    applicantIsContractor: '',
    applicantIsOwner: '',
    
    // Contractor Information
    contractorName: '',
    siteContact: '',
    contractorPhone: '',
    contractorEmail: '',
    contractorStreetAddress: '',
    contractorStreetAddressLine2: '',
    contractorCity: '',
    contractorState: '',
    contractorZip: '',
    
    // Work Location
    workLatitude: '',
    workLongitude: '',
    addressOfWork: '',
    lotNumber: '',
    subdivision: '',
    parcelNumber: '',
    
    // ROW Work Details
    excavationTypes: [],
    locationOfWork: '',
    sidewalks: '',
    treeRootsDisturbed: '',
    driveApproach: '',
    numberOfCutsHoles: '',
    descriptionOfWork: '',
    
    // Closures
    closureTypes: [],
    
    // Work Timeline
    startDate: '',
    completionDate: '',
    
    // Signature
    printedName: '',
    signatureDate: ''
  });

  // Auto-save form data to localStorage
  useEffect(() => {
    const saveDraft = () => {
      const draftData = {
        formData,
        detailedDrawing: detailedDrawing ? { name: detailedDrawing.name } : null,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('rowPermitDraft', JSON.stringify(draftData));
    };

    // Debounce save to avoid too frequent writes
    const timeoutId = setTimeout(saveDraft, 500);
    return () => clearTimeout(timeoutId);
  }, [formData, detailedDrawing]);

  useEffect(() => {
    // Load draft data on mount (if not coming from clone action)
    if (!location.state?.prefillData) {
      const savedDraft = localStorage.getItem('rowPermitDraft');
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          // Check if draft is recent (within 7 days)
          const draftDate = new Date(draft.timestamp);
          const daysSinceDraft = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
          
          if (daysSinceDraft < 7) {
            setFormData(draft.formData);
            // Note: File objects can't be restored from localStorage, only metadata
            // Silently restore - no notification needed
          } else {
            // Clear old draft
            localStorage.removeItem('rowPermitDraft');
          }
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      }
    } else {
      // Pre-fill form if coming from clone action
      setFormData(location.state.prefillData);
      if (location.state.rejectionReason) {
        alert(`Previous application was rejected: ${location.state.rejectionReason}\n\nPlease review and correct your information before resubmitting.`);
      }
      // Clear any existing draft when using prefill data
      localStorage.removeItem('rowPermitDraft');
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'excavationTypes' || name === 'closureTypes') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
            ? [...prev[name], value]
            : prev[name].filter(item => item !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDetailedDrawing(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const multipartData = new FormData();

      // Enrich with jurisdiction info based on selected location
      const selectedLocationRaw = localStorage.getItem('selectedCounty');
      let submissionFields = { ...formData };
      if (selectedLocationRaw) {
        try {
          const loc = JSON.parse(selectedLocationRaw);
          submissionFields = {
            ...submissionFields,
            jurisdictionCity: loc.city || '',
            jurisdictionCounty: loc.county || '',
            jurisdictionState: loc.state || '',
            jurisdictionStateAbbr: loc.stateAbbr || ''
          };
        } catch (err) {
          console.error('Failed to parse selectedCounty for ROW permit:', err);
        }
      }

      // Append text fields
      Object.entries(submissionFields).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          multipartData.append(key, JSON.stringify(value));
        } else {
          multipartData.append(key, value ?? '');
        }
      });

      // Append detailed drawing file
      if (detailedDrawing) {
        multipartData.append('detailedDrawing', detailedDrawing);
      }

      const response = await axios.post(`${API_BASE}/submit`, multipartData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Clear draft data on successful submission
        localStorage.removeItem('rowPermitDraft');
        navigate(`/status/${response.data.submissionId}`);
      }
    } catch (error) {
      alert('Failed to submit application: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>Right of Way (ROW) Permit Application</h2>
        {location.state?.originalSubmissionId && (
          <div className="prefill-notice">
            <strong>ℹ️ Pre-filled from previous application</strong>
            <p>Original Submission ID: {location.state.originalSubmissionId}</p>
            <p>Please review and correct the information before submitting.</p>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="form-section info-box">
          <h3>Terms and Conditions</h3>
          <ul style={{ paddingLeft: '1.5rem', color: '#555' }}>
            <li>The Engineering Department has up to ten (10) working days to review and approve/deny this permit application.</li>
            <li>Requests for additional information due to an incomplete application will restart the review process.</li>
            <li>A detailed drawing showing location and extent of work must be submitted for any Right-of-Way work.</li>
            <li>Any road, lane, rolling, sidewalk or trail closures require submission of a Maintenance of Traffic (MOT) plan and Board of Public Works and Safety approval, PRIOR to work commencing.</li>
            <li>Excavation must be completed (permanent patch) ten (10) working days after the start of excavation.</li>
            <li>Applicant must notify the Engineering Department (rowpermits@westlafayette.in.gov) one day in advance of commencement of work, indicating the permit number and address of work.</li>
          </ul>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* APPLICANT INFORMATION */}
          <div className="form-section">
            <h3>Applicant Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Applicant Name *</label>
                <input
                  type="text"
                  name="applicantName"
                  value={formData.applicantName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant Phone Number *</label>
                <input
                  type="tel"
                  name="applicantPhone"
                  value={formData.applicantPhone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant Email *</label>
                <input
                  type="email"
                  name="applicantEmail"
                  value={formData.applicantEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Applicant Street Address *</label>
                <input
                  type="text"
                  name="applicantStreetAddress"
                  value={formData.applicantStreetAddress}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant Street Address Line 2</label>
                <input
                  type="text"
                  name="applicantStreetAddressLine2"
                  value={formData.applicantStreetAddressLine2}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Applicant City *</label>
                <input
                  type="text"
                  name="applicantCity"
                  value={formData.applicantCity}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant State *</label>
                <input
                  type="text"
                  name="applicantState"
                  value={formData.applicantState}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant Zip *</label>
                <input
                  type="text"
                  name="applicantZip"
                  value={formData.applicantZip}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Applicant is the Contractor? *</label>
                <select
                  name="applicantIsContractor"
                  value={formData.applicantIsContractor}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="form-group">
                <label>Applicant is the Owner? *</label>
                <select
                  name="applicantIsOwner"
                  value={formData.applicantIsOwner}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* CONTRACTOR INFORMATION */}
          {formData.applicantIsContractor === 'no' && (
            <div className="form-section">
              <h3>Contractor Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Contractor Name *</label>
                  <input
                    type="text"
                    name="contractorName"
                    value={formData.contractorName}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Site Contact (First and Last Name) *</label>
                  <input
                    type="text"
                    name="siteContact"
                    value={formData.siteContact}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor Phone Number *</label>
                  <input
                    type="tel"
                    name="contractorPhone"
                    value={formData.contractorPhone}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor Email *</label>
                  <input
                    type="email"
                    name="contractorEmail"
                    value={formData.contractorEmail}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Contractor Street Address *</label>
                  <input
                    type="text"
                    name="contractorStreetAddress"
                    value={formData.contractorStreetAddress}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor Street Address Line 2</label>
                  <input
                    type="text"
                    name="contractorStreetAddressLine2"
                    value={formData.contractorStreetAddressLine2}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor City *</label>
                  <input
                    type="text"
                    name="contractorCity"
                    value={formData.contractorCity}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor State / Province *</label>
                  <input
                    type="text"
                    name="contractorState"
                    value={formData.contractorState}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
                <div className="form-group">
                  <label>Contractor Postal / Zip Code *</label>
                  <input
                    type="text"
                    name="contractorZip"
                    value={formData.contractorZip}
                    onChange={handleChange}
                    required={formData.applicantIsContractor === 'no'}
                  />
                </div>
              </div>
            </div>
          )}

          {/* WORK LOCATION */}
          <div className="form-section">
            <h3>Work Location</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="text"
                  name="workLatitude"
                  value={formData.workLatitude}
                  onChange={handleChange}
                  placeholder="Click map to select"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="text"
                  name="workLongitude"
                  value={formData.workLongitude}
                  onChange={handleChange}
                  placeholder="Click map to select"
                />
              </div>
              <div className="form-group full-width">
                <label>Address of Work *</label>
                <input
                  type="text"
                  name="addressOfWork"
                  value={formData.addressOfWork}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Lot Number</label>
                <input
                  type="text"
                  name="lotNumber"
                  value={formData.lotNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Subdivision (if applicable)</label>
                <input
                  type="text"
                  name="subdivision"
                  value={formData.subdivision}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Parcel Number</label>
                <input
                  type="text"
                  name="parcelNumber"
                  value={formData.parcelNumber}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          {/* ROW WORK DETAILS */}
          <div className="form-section">
            <h3>ROW Work Details</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Excavation Types *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {['No Excavation', 'Cut', 'Trench', 'Bore', 'Other'].map(type => (
                    <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        name="excavationTypes"
                        value={type.toLowerCase().replace(' ', '_')}
                        checked={formData.excavationTypes.includes(type.toLowerCase().replace(' ', '_'))}
                        onChange={handleChange}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group full-width">
                <label>Location of Work *</label>
                <select
                  name="locationOfWork"
                  value={formData.locationOfWork}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="street">Street</option>
                  <option value="alley">Alley</option>
                  <option value="grass_soft_surface">Grass/Soft Surface</option>
                  <option value="sidewalks">Sidewalks</option>
                  <option value="trail">Trail</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sidewalks</label>
                <select
                  name="sidewalks"
                  value={formData.sidewalks}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="new">New</option>
                  <option value="replace">Replace</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tree Roots Will Be Disturbed</label>
                <select
                  name="treeRootsDisturbed"
                  value={formData.treeRootsDisturbed}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="form-group">
                <label>Drive Approach</label>
                <select
                  name="driveApproach"
                  value={formData.driveApproach}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="new">New</option>
                  <option value="replace">Replace</option>
                  <option value="widen">Widen</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div className="form-group">
                <label>Number of Cuts/Holes *</label>
                <input
                  type="number"
                  name="numberOfCutsHoles"
                  value={formData.numberOfCutsHoles}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Description of Work *</label>
                <textarea
                  name="descriptionOfWork"
                  value={formData.descriptionOfWork}
                  onChange={handleChange}
                  rows="4"
                  maxLength="255"
                  required
                />
                <p className="form-note-small">{formData.descriptionOfWork.length}/255 characters</p>
              </div>
            </div>
          </div>

          {/* CLOSURES */}
          <div className="form-section">
            <h3>Closures</h3>
            <p className="form-note">*Items marked with asterisk require Board of Public Works and Safety approval PRIOR to work commencing</p>
            <div className="form-group full-width">
              <label>Closure Types (Please select all that apply)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { value: 'full_road', label: 'Full Road *' },
                  { value: 'lane', label: 'Lane *' },
                  { value: 'sidewalk', label: 'Sidewalk *' },
                  { value: 'trail', label: 'Trail *' },
                  { value: 'on_street_parking', label: 'On-Street Parking' },
                  { value: 'emergency_road_closure', label: 'Emergency Road Closure, retroactive *' }
                ].map(closure => (
                  <label key={closure.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="closureTypes"
                      value={closure.value}
                      checked={formData.closureTypes.includes(closure.value)}
                      onChange={handleChange}
                    />
                    <span>{closure.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* WORK TIMELINE */}
          <div className="form-section">
            <h3>Work Timeline</h3>
            <p className="form-note">Remember: Excavation must be completed (permanent patch) within 10 working days of start</p>
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Completion Date *</label>
                <input
                  type="date"
                  name="completionDate"
                  value={formData.completionDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* REQUIRED DOCUMENTS */}
          <div className="form-section">
            <h3>Required Documents</h3>
            <div className="form-group full-width">
              <label>Detailed Drawing - Location and Extent of Work *</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                onChange={handleFileChange}
                required
              />
              {detailedDrawing && (
                <p className="file-selected">Selected: {detailedDrawing.name}</p>
              )}
            </div>
          </div>

          {/* SIGNATURE */}
          <div className="form-section">
            <h3>Signature</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Printed Name *</label>
                <input
                  type="text"
                  name="printedName"
                  value={formData.printedName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="signatureDate"
                  value={formData.signatureDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ROWPermitForm;
